import {
  Box,
  Button,
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Fade,
  Flex,
  IconButton,
  InputGroup,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { ArrowRightFromLine, ChevronDown, X } from 'lucide-react';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import pickBy from 'lodash-es/pickBy.js';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Suspense, useCallback, useMemo, useState, useEffect } from 'react';
import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';
import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { UserRole } from '@duke-hq/svc-api-db/schemas/enums/UserRole.schema';

import { emptyPaginationResponse, parseSortOrder, SortOrder } from '@duke-hq/lib/pagination';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';
import { MAX_SEARCH_LENGTH } from '@duke-hq/shared-constants/searchCaps';
import { ProviderConsultantFormModal } from './ProviderConsultantFormModal';
import { ProviderConsultantsTable } from './ProviderConsultantsTable';
import { ProviderConsultantDetails } from './ProviderConsultantDetails';
import { LocationError } from '../Locations/LocationError';
import { withPermission } from '../../withPermission';
import ProviderLocationsFilters from './ProviderLocationsFilters';
import { iconSize } from '@duke-hq/ui/constants/scalable';

interface SortFilters {
  orderBy: string | undefined;
  sort: SortOrder | undefined;
  search?: string | undefined;
  page?: string | undefined;
  status?: string | undefined;
  role?: Exclude<UserRole, 'STUDENT' | 'ADMIN' | 'ASSESSOR'> | undefined;
  sites?: string | undefined;
  regions?: string | undefined;
}

const statusOptions = [
  { label: 'ENABLED', value: 'ENABLED' },
  { label: 'DISABLED', value: 'DISABLED' },
];

export const ProviderConsultants = withPermission('canViewProviders', () => {
  const { providerId, consultantId } = useParams<{
    providerId: string;
    consultantId: string;
  }>();
  const { currentPage, pageSize, setPage } = usePagination();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const navigate = useNavigate();
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();

  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: 'name',
    sort: SortOrder.ASC,
    page: '1',
    sites: '',
    regions: '',
  });

  const querySearch = searchParams.get('search') ?? '';

  const getURLFilters = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { page, search, status, orderBy, sort, ...rest } = urlFilters;
    return {
      ...rest,
      orderBy: orderBy ?? 'name',
      sort: parseSortOrder(sort) ?? SortOrder.ASC,
      status: status || undefined,
      page: page ?? '1',
      sites: urlFilters.sites ?? '',
      regions: urlFilters.regions ?? '',
    };
  }, [searchParams]);

  const handleFilterChange = useCallback(
    (newFilters: Partial<SortFilters>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters },
        (value) => value != null && value != '',
      );

      if (updatedFilters.page != null && updatedFilters.page === '1') {
        delete updatedFilters.page;
      }

      const newSearchParams = new URLSearchParams(updatedFilters);
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const handleStatus = useCallback(
    (status: string | undefined) => {
      setStatusFilter(status);
      handleFilterChange({ ...getURLFilters, status, page: '1' });
    },
    [handleFilterChange, getURLFilters],
  );

  const { isOpen, onOpen } = useDisclosure({
    isOpen: !(consultantId == null),
  });

  const onCloseHandler = useCallback(() => {
    navigate({
      pathname: `/providers/${providerId}/consultants`,
      search: location.search, // preserve full query string
    });
  }, [navigate, providerId]);

  useEffect(() => {
    if (consultantId != null) {
      onOpen();
    }
  }, [consultantId, onOpen]);

  if (providerId == null) {
    return <LocationError message={t`An error occurred, providerId is required.`} />;
  }

  const { data: consultants, isFetching } =
    trpcAdmin.providers.user.listConsultantsWithPagination.useQuery(
      {
        search: querySearch.trim(),
        filters: getURLFilters,
        providerId: Number(providerId),
        pagination: {
          page: Number(currentPage),
          limit: pageSize,
        },
      },
      {
        enabled: Boolean(providerId),
        initialData: emptyPaginationResponse(),
        placeholderData: (previousData) => previousData,
      },
    );

  return (
    <>
      {consultantId != null ? (
        <Drawer isOpen={isOpen} onClose={onCloseHandler} size="xl">
          <DrawerOverlay />
          <DrawerContent>
            <DrawerCloseButton onClick={onCloseHandler} />
            <Suspense fallback={<CenteredSpinner />}>
              <ProviderConsultantDetails />
            </Suspense>
          </DrawerContent>
        </Drawer>
      ) : null}

      <TableViewLayout
        pageHeader={[
          !isExpanded && (
            <IconButton
              aria-label="expand secondary sidebar"
              icon={<ArrowRightFromLine />}
              variant="tertiary"
              onClick={toggleExpandSubSidebar}
              size="md"
            />
          ),
          <PageHeading key="page-heading">
            <Trans>Consultants</Trans>
          </PageHeading>,
        ]}
        pageActions={[
          <ProviderConsultantFormModal
            key={'teamMemberFormModal'}
            providerId={Number(providerId)}
            onClose={onCloseHandler}
          />,
        ]}
        filters={
          <Flex w="full" gap={2}>
            <Menu>
              <MenuButton
                as={Button}
                variant="outline"
                px={2}
                width="auto"
                height="8"
                colorScheme="gray"
                rightIcon={
                  statusFilter ? (
                    <Box
                      p={0}
                      minW="fit-content"
                      onClick={(e) => {
                        e.preventDefault();
                        handleStatus(undefined);
                      }}
                      ml={1}
                    >
                      <X size={iconSize.md} />
                    </Box>
                  ) : (
                    <ChevronDown size={iconSize.md} />
                  )
                }
              >
                {statusFilter || 'Status'}
              </MenuButton>
              <MenuList>
                {statusOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    onClick={(e) => {
                      e.preventDefault();
                      handleStatus(option.value);
                    }}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>

            <ProviderLocationsFilters
              onChange={(sites, regions) => {
                handleFilterChange({
                  sites: sites.length > 0 ? sites.join(',') : undefined,
                  regions: regions.length > 0 ? regions.join(',') : undefined,
                  page: '1',
                });
              }}
              providerId={Number(providerId)}
            />

            <InputGroup maxW="xs" size="sm">
              <SearchInput maxLength={MAX_SEARCH_LENGTH} />
            </InputGroup>
          </Flex>
        }
        tableActionsLeft={
          <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
            <Plural
              value={consultants?.pagination?.totalCount ?? 0}
              zero="0 consultants"
              one="1 consultant"
              other="# consultants"
            />
          </Text>
        }
        table={
          <>
            <ProviderConsultantsTable
              consultants={consultants?.items ?? []}
              onFilters={handleFilterChange}
              urlParams={getURLFilters}
            />
            {isFetching ? (
              <Fade
                in={true}
                transition={{
                  enter: {
                    delay: 0.3,
                    duration: 0,
                  },
                }}
              >
                <Center p={8}>
                  <Spinner size="xl" transitionDelay={'2900ms'} />
                </Center>
              </Fade>
            ) : consultants?.items.length === 0 ? (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No consultants found</Trans>
                </Text>
              </Center>
            ) : null}
          </>
        }
        pagination={
          consultants != null && consultants?.items.length > 0 ? (
            <Pagination pagination={consultants.pagination} onPageChange={setPage} />
          ) : null
        }
      />
    </>
  );
});
